import { LoaderLine } from '@components/LoaderBar';
import { useMetadata } from '@contexts/org-metadata';
import { HoverBorderButton, StyledButton } from '@elements/Button';
import { MuteText } from '@elements/Common';
import { NumberMaskInput } from '@elements/CustomMaskInput';
import { CountryDropdownStyle } from '@elements/Dropdown';
import { DarkPopup } from '@elements/Popup';
import { useAsyncDebounce } from '@hooks/debounceAsync';
import { globalItemHook, predictionDayLeftHook } from '@hooks/prediction';
import {
  POGlobalPopupContent,
  tooltTipsText,
} from '@page-components/condensed-timeline/Chart';
import {
  CalculationAccordion,
  Formula,
  FormulaNoData,
  RegionalAccordion,
} from '@page-components/condensed-timeline/POSeasonality';
import SeasonalityChart from '@page-components/condensed-timeline/sales-forecast/SeasonalityChart';
import SeasonalityNoData from '@page-components/condensed-timeline/sales-forecast/SeasonalityNoData';
import {
  ChartRenderer,
  ChartRendererNoData,
} from '@page-components/purchase-order/timeline/chart';
import { CommonUtility } from '@utility/common';
import {
  CommonConstant,
  SALES_CHART_TYPES,
  SALES_CHART_TYPE_DEFAULT_VALUE,
} from '@utility/constants/common';
import PredictionService from '@utility/services/prediction';
import { ToastMessage } from '@utility/toast';
import { Link } from 'react-router';
import {
  useNavigate,
  useParams,
  useSearchParams,
  useLocation,
} from 'react-router';
import { ArrowsClockwise, Info, Plus, Warning } from '@phosphor-icons/react';
import { useEffect, useMemo, useState } from 'react';
import { cn, Drawer, Image, SegmentedTabs } from '@skupreme/shared/ui';
import { POWindowVelocityRenderer } from './timeline/VelocityRederer';
import {
  KEYS_TIMELINE_TABS,
  TIMELINE_TABS,
  TabContent,
  TabInformation,
} from './timeline/common';

const getImage = (product, imageKey) =>
  product?.[imageKey]?.display_image ||
  product?.[imageKey] ||
  CommonConstant.defaultImage;

const TAB_ITEMS = KEYS_TIMELINE_TABS.map(key => ({
  id: key,
  label: TIMELINE_TABS[key],
}));

const STAT = 'sk:flex sk:items-center';

const getPrimaryDaysFromProviderSalesVelocity = providerSalesVelocities => {
  let value = '0';
  if (CommonUtility.isValidObject(providerSalesVelocities)) {
    const keys = Object.keys(providerSalesVelocities).filter(d =>
      d.includes('Day'),
    );
    if (CommonUtility.isValidArray(keys)) {
      keys.forEach(d => {
        const { isPrimary, days } = providerSalesVelocities?.[d] ?? {};
        if (isPrimary && days !== undefined) {
          value = days;
        }
      });
    }
  }
  // @ts-expect-error - TS2322: Type 'number' is not assignable to type 'string'.
  value = Number.parseInt(`${value}` || '0', 10);
  return value;
};

export function CondensedTimelinePopup({
  itemId,
  destination,
  itemSrc,
  itemSKU,
  setCurrentItem,
  inputChanged,
  itemUPC,
  refresh: refreshItems,
  imageKey = 'p_images',
  predictionRedirection = null,
  readOnly = false,
}) {
  const {
    data: globalData,
    loading: globalLoading,
    refresh,
    marketplaceDropdown,
  } = globalItemHook(itemUPC, destination, itemSrc?.supplier_id);

  /* @ts-expect-error - TS2339: Property 'timeline' does not exist on type 'never[]'. */
  const { timeline } = useMemo(() => globalData || {}, [globalData]);

  const [regionalItem, setRegionalItem] = useState('');
  const [chartType, setChartType] = useState(SALES_CHART_TYPE_DEFAULT_VALUE);
  const [activeTab, setActiveTab] = useState(KEYS_TIMELINE_TABS[0]);
  const [providerVelocities, setProviderVelocities] = useState([]);
  const [value, setValue] = useState('0');

  const [refreshKey, setRefreshKey] = useState(0);

  const navigate = useNavigate();

  const { currentItemRegion, regionItem } = useMemo(() => {
    // @ts-expect-error - TS2339: Property 'regionalItemsList' does not exist on type 'never[]'.
    const item = (globalData.regionalItemsList || []).find(
      x => x?._id === regionalItem,
    );
    return { currentItemRegion: item?.marketplace_region_id, regionItem: item };
  }, [regionalItem]);

  const [activeVelocityCards, setActiveVelocityCards] = useState({});

  const currentBuffer = useMemo(() => {
    if (!currentItemRegion || !timeline) return {};

    return (
      timeline?.buffers_data?.find(
        x => x.marketplace_region_id === currentItemRegion,
      ) || {}
    );
  }, [timeline, currentItemRegion]);

  /* @ts-expect-error - TS2339: Property 'defaultRegionId' does not exist on type 'unknown'. */
  const { defaultRegionId } = useMetadata();

  const { data, loading, forceRefreshUPC, setLoading, softRefresh } =
    predictionDayLeftHook(
      regionalItem,
      currentItemRegion,
      null,
      timeline?.regional_providers?.[0]?.fulfillment_providers?.[0]
        ?.fulfillment_provider_id,
      refreshItems,
    );

  const [defaultValues, setDefaultValues] = useState({
    moq: '',
    lead_time: '',
    transit_time: '',
    minimum_desired_stock: '',
  });

  const [tabs, setTabs] = useState({
    season: true,
    velocity: true,
    calculation: true,
    regionalBreakdown: true,
  });

  const [isDefault, setIsDefault] = useState({
    source_minimum_buffer: false,
  });

  const [addEventCount, setAddEventCount] = useState(0);

  const modalClose = () => {
    setCurrentItem(null);
  };

  const choosePrediction = e => {
    e.preventDefault();
    if (predictionRedirection) {
      navigate(predictionRedirection);
    } else {
      ToastMessage.success(
        `Using the suggested quantity value for item ${itemSKU}`,
      );
      inputChanged(itemSrc);
      modalClose();
    }
  };

  const refreshTimeline = async () => {
    await softRefresh();
    setRefreshKey(refreshKey => refreshKey + 1);
    await refresh();
    refreshItems();
  };

  const hardRefreshTimeline = async () => {
    await forceRefreshUPC(itemUPC);
    setRefreshKey(refreshKey => refreshKey + 1);
    await refresh();
    refreshItems();
  };

  const toggleTab = (tabName, value) => {
    setTabs({
      ...tabs,
      [tabName]: value,
    });
  };

  const predictedOrder = useMemo(
    () =>
      // @ts-expect-error - TS2339: Property 'order_predictions' does not exist on type 'never[]'.
      data?.order_predictions?.[0] || { buffer_data: data.orderTimeLine || {} },
    [data],
  );

  useEffect(() => {
    if (data.length < 1) return;
    const velocityCards = {};
    const providerSalesVelocities = [];
    // @ts-expect-error - TS2339: Property 'primary_warehouse' does not exist on type 'never[]'.
    const primaryWarehouse = data.primary_warehouse;

    const defaults = {
      source_minimum_buffer: true,
    };

    // @ts-expect-error - TS2339: Property 'storageProviders' does not exist on type 'never[]'.
    for (let i = 0; i < data.storageProviders?.length; i += 1) {
      if (
        // @ts-expect-error - TS2339: Property 'storageProviders' does not exist on type 'never[]'.
        !data.storageProviders[i].fulfillment_provider_id ||
        // @ts-expect-error - TS2339: Property 'storageProviders' does not exist on type 'never[]'.
        data.storageProviders[i]?.is_primary_hybrid
      )
        continue;

      const provider = {
        isPrimaryProvider:
          // @ts-expect-error - TS2339: Property 'storageProviders' does not exist on type 'never[]'.
          data.storageProviders[i].fulfillment_provider_id ===
          // @ts-expect-error - TS2339: Property 'primary_warehouse' does not exist on type 'never[]'.
          data.primary_warehouse,
        // @ts-expect-error - TS2339: Property 'storageProviders' does not exist on type 'never[]'.
        value: data.storageProviders[i].providerSalesVelocity,
        days: getPrimaryDaysFromProviderSalesVelocity(
          // @ts-expect-error - TS2339: Property 'storageProviders' does not exist on type 'never[]'.
          data.storageProviders[i].providerSalesVelocities,
        ),
        // @ts-expect-error - TS2339: Property 'storageProviders' does not exist on type 'never[]'.
        is_warehouse: data.storageProviders[i].is_warehouse,
        fulfillment_provider_name:
          // @ts-expect-error - TS2339: Property 'storageProviders' does not exist on type 'never[]'.
          data.storageProviders[i].fulfillment_provider_name,
        fulfillment_provider_id:
          // @ts-expect-error - TS2339: Property 'storageProviders' does not exist on type 'never[]'.
          data.storageProviders[i].fulfillment_provider_id,
        isSystem: !CommonUtility.isNotNull(
          // @ts-expect-error - TS2339: Property 'storageProviders' does not exist on type 'never[]'.
          data.storageProviders[i].providerSalesVelocities?.manualSales?.value,
        ),
        // @ts-expect-error - TS2339: Property 'storageProviders' does not exist on type 'never[]'.
        adjustedSalesVelocity: data.storageProviders[i].adjustedSalesVelocity,
        // @ts-expect-error - TS2339: Property 'storageProviders' does not exist on type 'never[]'.
        region: data.storageProviders[i].marketplaceRegionName,
        // @ts-expect-error - TS2339: Property 'storageProviders' does not exist on type 'never[]'.
        is_fulfillable: data.storageProviders[i].is_fulfillable,
        // @ts-expect-error - TS2339: Property 'storageProviders' does not exist on type 'never[]'.
        present_inventory: data.storageProviders[i].present_inventory,
        // @ts-expect-error - TS2339: Property 'storageProviders' does not exist on type 'never[]'.
        unknownProvider: !!data.storageProviders[i].unknownVelocityExists,
      };
      // @ts-expect-error - TS2339: Property 'storageProviders' does not exist on type 'never[]'.
      if (data.storageProviders[i].unknownVelocityExists) {
        let total = 0;
        // @ts-expect-error - TS2339: Property 'storageProviders' does not exist on type 'never[]'.
        Object.values(data.storageProviders[i].providerSalesVelocity).forEach(
          market => {
            // @ts-expect-error - TS18046: 'market' is of type 'unknown'.
            total += +market.value;
          },
        );
        provider.value = total;
        // @ts-expect-error - TS2339: Property 'unknownProviderBreakdown' does not exist on type '{ isPrimaryProvider:...
        provider.unknownProviderBreakdown =
          // @ts-expect-error - TS2339: Property 'storageProviders' does not exist on type 'never[]'.
          data.storageProviders[i].providerSalesVelocity;
      }

      if (
        // @ts-expect-error - TS2339: Property 'minimumDesiredStock' does not exist on type 'never[]'.
        data.minimumDesiredStock &&
        // @ts-expect-error - TS2339: Property 'minimumDesiredStock' does not exist on type 'never[]'.
        data.minimumDesiredStock[primaryWarehouse]
      ) {
        defaults.source_minimum_buffer = false;
      }

      // @ts-expect-error - TS2339: Property 'defaultVelocityWindow' does not exist on type 'never[]'.
      if (data?.defaultVelocityWindow?.[primaryWarehouse] === 0) {
        defaults.source_minimum_buffer = true;
      }

      // @ts-expect-error - TS2345: Argument of type '{ isPrimaryProvider: boolean; value: any; days: string; is_war...
      providerSalesVelocities.push(provider);
      // @ts-expect-error - TS2339: Property 'storageProviders' does not exist on type 'never[]'.
      velocityCards[data.storageProviders[i].fulfillment_provider_id] = false;
    }
    setIsDefault(defaults);
    setActiveVelocityCards(velocityCards);
    setProviderVelocities(providerSalesVelocities);
  }, [data, !!itemSrc]);

  const activateVelocityWindow = id => {
    const newCards = { ...activeVelocityCards };
    Object.keys(newCards).forEach(SP_id => {
      newCards[SP_id] = false;
    });
    setActiveVelocityCards({
      ...newCards,
      [id]: true,
    });
  };

  const { primaryVelocity, adjusted } = useMemo(() => {
    let max = 0;
    let adjusted = false;
    // @ts-expect-error - TS2339: Property 'storageProviders' does not exist on type 'never[]'.
    data?.storageProviders?.forEach(provider => {
      if (
        // @ts-expect-error - TS2339: Property 'primary_warehouse' does not exist on type 'never[]'.
        provider.fulfillment_provider_id === data.primary_warehouse &&
        !provider.is_primary_hybrid
      ) {
        adjusted = provider.adjustedSalesVelocity;
        max = provider.providerSalesVelocity;
      }
    });
    return { primaryVelocity: adjusted ?? max, adjusted };
  }, [data]);

  const regionalFormattedData = useMemo(() => {
    const regionObject = {};
    let totalDaysLeft = 0;
    let globalInventory = 0;
    let maxVelocityInAllRegions = 0;
    timeline?.regional_providers?.forEach(region => {
      let destinationName = '';
      let predictedDaysLeft = 0;
      let maxQty = 0;
      let maxVelocity = -1;
      let totalInventory = 0;
      let providerDaysLeft = 0;
      let defaultMinDesiredStock = 0;

      const primaryProvider = region.fulfillment_providers.find(
        x => x.fulfillment_provider_id === region.primary_warehosue,
      );
      defaultMinDesiredStock = primaryProvider?.inventory_buffer_stock;
      let sumOfInventory = 0;
      region.fulfillment_providers.forEach(provider => {
        const velocity = provider.adjusted_velocity || provider.sales_velocity;
        if (velocity) sumOfInventory += provider.inventory;
      });

      region.fulfillment_providers.forEach(provider => {
        const velocity = provider.adjusted_velocity || provider.sales_velocity;
        if (velocity > maxVelocity) {
          maxVelocity = velocity;
        }
        if (!destinationName || provider.qty) {
          destinationName = provider.fulfillment_provider_name;
        }

        const ratio = provider.inventory / sumOfInventory;
        totalInventory += provider.inventory;
        totalInventory += provider.reserved_inventory || 0;
        maxQty += provider.qty;
        predictedDaysLeft += provider.qtyInDays;

        if (velocity)
          providerDaysLeft += (provider.days_left || 0) * ratio || 0;
      });

      globalInventory += totalInventory;

      regionObject[region.region_name] = {
        maxQty,
        totalInventory,
        maxVelocity,
        destinationName,
        predictedDaysLeft,
        providerDaysLeft: Math.round(providerDaysLeft || 0),
        defaultMinDesiredStock,
        primaryProvider: region.primary_warehosue,
      };
    });

    Object.values(regionObject).forEach(region => {
      // @ts-expect-error - TS18046: 'region' is of type 'unknown'.
      const ratio = region.totalInventory / globalInventory;
      // @ts-expect-error - TS18046: 'region' is of type 'unknown'.
      totalDaysLeft += region.providerDaysLeft * ratio;
      // @ts-expect-error - TS18046: 'region' is of type 'unknown'.
      if (maxVelocityInAllRegions < region.maxVelocity) {
        // @ts-expect-error - TS18046: 'region' is of type 'unknown'.
        maxVelocityInAllRegions = region.maxVelocity;
      }
    });

    // @ts-expect-error - TS2339: Property 'totalDaysLeft' does not exist on type '{}'.
    regionObject.totalDaysLeft = Math.round(totalDaysLeft || 0);
    // @ts-expect-error - TS2339: Property 'maxVelocityInAllRegions' does not exist on type '{}'.
    regionObject.maxVelocityInAllRegions = maxVelocityInAllRegions;

    return regionObject;
  }, [timeline]);

  useEffect(() => {
    let regionPrimary = null;
    Object.keys(regionalFormattedData).forEach(region => {
      if (
        // @ts-expect-error - TS2538: Type 'null' cannot be used as an index type.
        regionalFormattedData[regionPrimary]?.maxQty ===
        regionalFormattedData[region]?.maxQty
      ) {
        if (
          regionalFormattedData[region]?.maxVelocity >
          // @ts-expect-error - TS2538: Type 'null' cannot be used as an index type.
          regionalFormattedData[regionPrimary]?.maxVelocity
        ) {
          // @ts-expect-error - TS2322: Type 'string' is not assignable to type 'null'.
          regionPrimary = region;
        }
      } else if (
        regionalFormattedData[region]?.maxQty >
        // @ts-expect-error - TS2538: Type 'null' cannot be used as an index type.
        (regionalFormattedData[regionPrimary]?.maxQty || 0)
      ) {
        // @ts-expect-error - TS2322: Type 'string' is not assignable to type 'null'.
        regionPrimary = region;
      }
    });

    // @ts-expect-error - TS2339: Property 'regionalItemsList' does not exist on type 'never[]'.
    let item = (globalData.regionalItemsList || []).find(
      x => x.marketplace_region_name === regionPrimary,
    );

    if (!item) {
      // @ts-expect-error - TS2339: Property 'regionalItemsList' does not exist on type 'never[]'.
      item = (globalData.regionalItemsList || []).find(
        x => x.marketplace_region_id === defaultRegionId,
      );
    }

    if (item) {
      setRegionalItem(item?._id);
    } else if (marketplaceDropdown.length > 0) {
      const isMissing =
        // @ts-expect-error - TS2339: Property 'value' does not exist on type 'never'.
        !regionalItem || !marketplaceDropdown.some(d => d.value === regionItem);
      if (isMissing) {
        // @ts-expect-error - TS2339: Property 'value' does not exist on type 'never'.
        setRegionalItem(marketplaceDropdown?.[0]?.value);
      }
    }
  }, [
    defaultRegionId,
    // @ts-expect-error - TS2339: Property 'value' does not exist on type 'never'.
    marketplaceDropdown?.[0]?.value,
    // @ts-expect-error - TS2339: Property 'totalDaysLeft' does not exist on type '{}'.
    regionalFormattedData.totalDaysLeft,
    // @ts-expect-error - TS2339: Property 'regionalItemsList' does not exist on type 'never[]'.
    globalData?.regionalItemsList?.length || 0,
  ]);

  const regionName = marketplaceDropdown.find(
    // @ts-expect-error - TS2339: Property 'value' does not exist on type 'never'.
    x => x.value === regionalItem,
    // @ts-expect-error - TS2339: Property 'name' does not exist on type 'never'.
  )?.name;

  const currentStock = useMemo(
    () => regionalFormattedData[regionName] || {},
    [regionalFormattedData, marketplaceDropdown, regionalItem],
  );

  const national = useMemo(() => {
    const regionName = marketplaceDropdown.find(
      // @ts-expect-error - TS2339: Property 'value' does not exist on type 'never'.
      x => x.value === regionalItem,
      // @ts-expect-error - TS2339: Property 'name' does not exist on type 'never'.
    )?.name;
    return regionName === timeline?.supplier_region;
  }, [timeline, marketplaceDropdown, regionalItem]);

  const offers = useMemo(() => {
    let seasonsPeaks = [];
    let lightningDeals = [];
    if (!data) return { seasonsPeaks, lightningDeals };

    // @ts-expect-error - TS2339: Property 'seasonalSpike' does not exist on type 'never[]'.
    seasonsPeaks = data.seasonalSpike || [];
    // @ts-expect-error - TS2339: Property 'lightningDeal' does not exist on type 'never[]'.
    lightningDeals = data.lightningDeal || [];

    return { seasonsPeaks, lightningDeals };
  }, [data]);

  const chartLoading = !!(globalLoading || loading);
  const memoLoading = useAsyncDebounce(chartLoading, 200);
  const minDesiredStock = useMemo(
    () =>
      CommonUtility.isNotNull(
        // @ts-expect-error - TS2339: Property 'minimumDesiredStock' does not exist on type 'never[]'.
        data?.minimumDesiredStock?.[data?.primary_warehouse],
      )
        ? // @ts-expect-error - TS2339: Property 'minimumDesiredStock' does not exist on type 'never[]'.
          data?.minimumDesiredStock?.[data?.primary_warehouse]
        : // @ts-expect-error - TS2339: Property 'default_min_desire_stock' does not exist on type 'never[]'.
          data?.default_min_desire_stock,
    [data],
  );

  useEffect(() => {
    const resetData = () => {
      setActiveVelocityCards({});
      setProviderVelocities([]);
    };

    if (!itemSrc) {
      resetData();
    }
  }, [itemSrc]);

  // @ts-expect-error - TS2339: Property 'regionalItemsList' does not exist on type 'never[]'.
  const regions = CommonUtility.isValidArray(globalData?.regionalItemsList)
    ? // @ts-expect-error - TS2339: Property 'regionalItemsList' does not exist on type 'never[]'.
      globalData?.regionalItemsList
    : [];

  const isOnSalesForecast = activeTab === 'SALES_FORECAST';
  const showSalesForecast =
    itemUPC && CommonUtility.isValidArray(marketplaceDropdown);
  const isOpen = !!itemSrc && !!itemId;

  useEffect(() => {
    setAddEventCount(0);
  }, [isOnSalesForecast, isOpen]);

  const dataTabContent = {
    INVENTORY_FLOW:
      regionalItem && CommonUtility.isValidObject(data as any) ? (
        <div className="sk:rounded-[5px] sk:bg-fill-tertiary sk:py-5">
          <ChartRenderer
            activateVelocityWindow={activateVelocityWindow}
            data={data}
          />
        </div>
      ) : (
        <ChartRendererNoData loading={chartLoading || memoLoading} />
      ),
    SALES_FORECAST: showSalesForecast ? (
      <SeasonalityChart
        addEventCount={addEventCount}
        chartType={chartType}
        forceRefresh={refreshTimeline}
        itemId={itemId}
        lightningDeal={offers?.lightningDeals}
        marketplaceDropdown={marketplaceDropdown}
        marketplaceRegionId={currentItemRegion}
        refreshTimeline={refreshTimeline}
        regionalItem={regionalItem}
        regionalItemsList={(globalData as any).regionalItemsList}
        regions={regions}
        seasonalSpike={offers?.seasonsPeaks}
        setAddEventCount={setAddEventCount}
        refreshKey={refreshKey}
        upc={itemUPC}
      />
    ) : (
      <SeasonalityNoData />
    ),
    VIEW_FORMULA: CommonUtility.isValidObject(data as any) ? (
      <div>
        <div>
          <RegionalAccordion
            regionalFormattedData={regionalFormattedData}
            regions={regions}
            tabs={tabs}
            timeline={timeline?.regional_providers}
            toggleTab={toggleTab}
          />
        </div>
        <div className="sk:mt-6 sk:pt-6">
          <Formula
            adjusted={adjusted}
            aliasName={(data as any)?.alias}
            currentBuffer={currentBuffer}
            defaultMinDesiredStock={minDesiredStock}
            defaultValues={defaultValues}
            destinationName={currentStock.destinationName}
            predictedOrder={predictedOrder}
            suggestedQty={currentStock?.maxQty}
            unitsPerCarton={(data as any)?.units_per_carton}
            velocityProvider={primaryVelocity}
          />
        </div>
      </div>
    ) : (
      <FormulaNoData />
    ),
  };

  const fulfillmentProviderId =
    timeline?.regional_providers?.[0]?.fulfillment_providers?.[0]
      ?.fulfillment_provider_id ?? '';
  const validProviderVelocities = CommonUtility.isValidArray(providerVelocities)
    ? providerVelocities
    : [];
  const isSetPredictionManual =
    validProviderVelocities.filter(
      // @ts-expect-error - TS2339: Property 'isPrimaryProvider' does not exist on type 'never'.
      d => d?.isPrimaryProvider === true && d?.value > 0,
    ).length === 0 && fulfillmentProviderId;

  const handleGenerate = async () => {
    // @ts-expect-error - TS2345: Argument of type 'boolean' is not assignable to parameter of type 'SetStateActio...
    setLoading(true);
    try {
      await PredictionService.updateVelocity({
        item_id: regionalItem || itemId,
        addManual: true,
        fulfillment_provider_id: fulfillmentProviderId,
        prediction: value || 0,
      });

      // @ts-expect-error - TS2345: Argument of type 'string' is not assignable to parameter of type 'SetStateAction...
      setDefaultValues(value);

      await CommonUtility.timeoutPromise(3000);
      await refreshTimeline();

      ToastMessage.success('Updated Sales Velocity');
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      // @ts-expect-error - TS2345: Argument of type 'boolean' is not assignable to parameter of type 'SetStateActio...
      setLoading(false);
    }
  };

  const selectedMarketplaceName = useMemo(
    // @ts-expect-error - TS2339: Property 'value' does not exist on type 'never'.
    () => marketplaceDropdown.find(d => d.value === regionalItem)?.name || '',
    [regionalItem, marketplaceDropdown],
  );

  const renderLabelWithMarketplace = label =>
    `${[label, selectedMarketplaceName ? `(${selectedMarketplaceName})` : '']
      .filter(d => d)
      .join(' ')}:`;

  // Update Loading Container Height As Direct CSS is Not Working
  useEffect(() => {
    if (chartLoading) {
      const containerEle =
        document.getElementsByClassName('main-container')?.[0];
      // @ts-expect-error - TS2339: Property 'offsetHeight' does not exist on type 'Element'.
      if (containerEle?.offsetHeight > 0) {
        const loadingEle = document.getElementById('main-container-loading');
        if (loadingEle) {
          // @ts-expect-error - TS2339: Property 'offsetHeight' does not exist on type 'Element'.
          loadingEle.style.minHeight = `${containerEle?.offsetHeight + 200}px`;
        }
      }
    }
  }, [chartLoading]);

  const totalInventoryWithReserved = useMemo(() => {
    let inventory = 0;
    Object.values(regionalFormattedData || {}).forEach(region => {
      // @ts-expect-error - TS2339: Property 'totalInventory' does not exist on type '{}'.
      if (region?.totalInventory) inventory += region.totalInventory;
    });
    return inventory;
  }, [regionalFormattedData]);

  const roundAndCommas = num => CommonUtility.numberWithCommas(Math.round(num));

  const globalSuggestedDaysLeft = useMemo(() => {
    if (
      timeline?.total_prediction &&
      // @ts-expect-error - TS2339: Property 'maxVelocityInAllRegions' does not exist on type '{}'.
      regionalFormattedData?.maxVelocityInAllRegions
    ) {
      return roundAndCommas(
        timeline?.total_prediction /
          // @ts-expect-error - TS2339: Property 'maxVelocityInAllRegions' does not exist on type '{}'.
          regionalFormattedData?.maxVelocityInAllRegions,
      );
    }
    return null;
  }, [timeline, regionalFormattedData]);

  const globalStockDaysLeft = useMemo(() => {
    if (
      // @ts-expect-error - TS2339: Property 'maxVelocityInAllRegions' does not exist on type '{}'.
      regionalFormattedData?.maxVelocityInAllRegions &&
      totalInventoryWithReserved
    ) {
      return roundAndCommas(
        totalInventoryWithReserved /
          // @ts-expect-error - TS2339: Property 'maxVelocityInAllRegions' does not exist on type '{}'.
          regionalFormattedData?.maxVelocityInAllRegions,
      );
    }
    return null;
  }, [regionalFormattedData, totalInventoryWithReserved]);

  const regionalSuggestedStockDaysLeft = useMemo(() => {
    if (timeline?.total_prediction && currentStock?.maxVelocity) {
      return roundAndCommas(
        timeline?.total_prediction / currentStock?.maxVelocity,
      );
    }
    return null;
  }, [timeline, currentStock]);

  const regionalStockDaysLeft = useMemo(() => {
    if (currentStock?.totalInventory && currentStock?.maxVelocity) {
      return roundAndCommas(
        currentStock?.totalInventory / currentStock?.maxVelocity,
      );
    }
    return null;
  }, [currentStock]);

  const isBusy = !!(chartLoading || memoLoading);

  const warningFooter = isSetPredictionManual && !isBusy && (
    <div className="sk:flex sk:w-full sk:flex-wrap sk:items-center sk:justify-between sk:gap-3">
      <div className="sk:flex sk:items-center sk:gap-2 sk:font-semibold sk:text-warning">
        <Warning size={18} className="sk:shrink-0" />
        Sales data for this product is insufficient to calculate sales velocity.
        Please enter manually to generate a prediction.
      </div>
      <div className="sk:flex sk:flex-nowrap sk:items-center sk:justify-end sk:gap-2">
        <div className="sk:flex sk:items-center sk:gap-1">
          <NumberMaskInput
            aria-label="Sales velocity in units per day"
            className="sk:w-[70px]"
            disabled={readOnly || isBusy}
            maskOptions={{
              allowDecimal: true,
              // @ts-expect-error - TS2353: Object literal may only specify known properties, and 'decimalLimit' does not ex...
              decimalLimit: 3,
              decimalSymbol: '.',
            }}
            onChange={val => setValue(val)}
            value={value}
          />
          <span className="sk:whitespace-nowrap sk:text-fg-tertiary">
            / Day
          </span>
        </div>
        <StyledButton
          className="sk:ml-2"
          onClick={handleGenerate}
          disabled={!!readOnly || isBusy || !(parseInt(value, 10) > 0)}
        >
          Generate
        </StyledButton>
        {(!!inputChanged || !!predictionRedirection) &&
          !!timeline?.total_prediction && (
            <StyledButton
              className="sk:whitespace-nowrap"
              onClick={e => choosePrediction(e)}
              disabled={!!readOnly || isBusy}
            >
              Use Suggested Qty
            </StyledButton>
          )}
      </div>
    </div>
  );

  const suggestionFooter = !isSetPredictionManual &&
    !isBusy &&
    !!timeline?.total_prediction &&
    currentStock?.maxQty > 0 && (
      <div className="sk:flex sk:w-full sk:flex-wrap sk:items-center sk:justify-between sk:gap-3">
        <div
          className={cn(
            'sk:flex sk:items-center sk:text-base',
            (inputChanged || predictionRedirection) && 'sk:text-success',
          )}
        >
          <span className="sk:mr-2 sk:font-semibold">Suggested Order:</span>{' '}
          <span>
            {CommonUtility.numberWithCommas(timeline?.total_prediction)} Units (
            {CommonUtility.numberWithCommas(currentStock?.predictedDaysLeft)}{' '}
            Days)
          </span>
          <DarkPopup
            trigger={<Info className="sk:ml-2" />}
            hover
            position="top center"
          >
            <POGlobalPopupContent
              timeline={timeline?.regional_providers}
              suggestedQty={timeline?.total_prediction}
            />
          </DarkPopup>
        </div>
        <div className="sk:flex sk:flex-nowrap sk:items-center sk:gap-2">
          <HoverBorderButton disabled={!!readOnly} onClick={() => modalClose()}>
            Cancel
          </HoverBorderButton>
          {(!!inputChanged || !!predictionRedirection) && (
            <StyledButton
              onClick={e => choosePrediction(e)}
              disabled={!!readOnly || isBusy}
            >
              Use Suggested Qty
            </StyledButton>
          )}
        </div>
      </div>
    );

  const title = (
    <>
      <Image
        alt=""
        size="md"
        src={getImage(itemSrc, imageKey)}
        className="sk:mr-2"
      />
      <Link
        to={`/app/catalogs/catalog/${itemId}/?tab=inventory-status`}
        className="sk:truncate sk:text-xl sk:font-bold sk:text-fg sk:hover:underline"
      >
        {itemSKU}
      </Link>
    </>
  );

  return (
    <Drawer
      isOpen={isOpen}
      onOpenChange={open => {
        if (!open) modalClose();
      }}
      size="full"
      title={title}
      footer={warningFooter || suggestionFooter || undefined}
    >
      <div className="sk:flex sk:flex-wrap sk:items-start sk:justify-between sk:gap-3 sk:pb-3 sk:text-[15px]">
        <div className="sk:flex sk:flex-wrap sk:gap-x-4 sk:gap-y-1">
          {!!timeline?.total_prediction && (
            <div className={STAT}>
              <MuteText className="sk:mr-1">Suggested Order</MuteText>
              <div className={STAT}>
                {CommonUtility.numberWithCommas(timeline?.total_prediction)}
                &nbsp;
                <div>Units</div>
                {!!globalSuggestedDaysLeft && (
                  <div className="sk:pl-2">
                    ({globalSuggestedDaysLeft} Days)
                  </div>
                )}
                <DarkPopup trigger={<Info className="sk:ml-2" />} hover>
                  <POGlobalPopupContent
                    timeline={timeline?.regional_providers}
                    suggestedQty={timeline?.total_prediction}
                  />
                </DarkPopup>
              </div>
            </div>
          )}
          <div className={STAT}>
            <MuteText className="sk:mr-1">Current Stock</MuteText>
            <div className={STAT}>
              {CommonUtility.numberWithCommas(totalInventoryWithReserved)}
              <div className="sk:ml-1">Units</div>
              {globalStockDaysLeft && (
                <div className="sk:pl-1">({globalStockDaysLeft} Days)</div>
              )}
            </div>
            <DarkPopup trigger={<Info className="sk:ml-1" />} hover>
              {tooltTipsText.currentStockGlobal}
            </DarkPopup>
          </div>
          <div className={STAT}>
            <MuteText className="sk:mr-1">Expected Stock</MuteText>
            <div className={STAT}>
              {CommonUtility.numberWithCommas(
                timeline?.total_expected_inventory,
              )}
              <div className="sk:ml-1">Units</div>
              <DarkPopup trigger={<Info className="sk:ml-1" />} hover>
                {tooltTipsText.expected_inventory} all regions aggregated
              </DarkPopup>
            </div>
          </div>
          {/* @ts-expect-error - TS2339: Property 'units_per_carton' does not exist on type 'never[]'... */}
          {!!data.units_per_carton && (
            <div className={STAT}>
              <MuteText className="sk:mr-1">Units Per Carton</MuteText>
              <div className={STAT}>
                {/* @ts-expect-error - TS2339: Property 'units_per_carton' does not exist on type 'never[]'... */}
                {data.units_per_carton}
                <DarkPopup
                  position="top right"
                  trigger={<Info className="sk:ml-1" />}
                  hover
                >
                  <div className="sk:whitespace-nowrap">
                    Units Per Carton{' '}
                    {/* @ts-expect-error - TS2339: Property 'alias' does not exist on type 'never[]'. */}
                    {!!data?.alias && (
                      <span className="sk:whitespace-nowrap">
                        {/* @ts-expect-error - TS2339: Property 'alias' does not exist on type 'never[]'. */}
                        of <strong>{data.alias}</strong>
                      </span>
                    )}{' '}
                    {/* @ts-expect-error - TS2339: Property 'units_per_carton' does not exist on type 'never[]'. */}
                    = {data.units_per_carton}
                  </div>
                </DarkPopup>
              </div>
            </div>
          )}
        </div>
        <HoverBorderButton
          aria-label="Refresh timeline"
          onClick={hardRefreshTimeline}
          loading={isBusy}
          disabled={readOnly || isBusy}
        >
          <ArrowsClockwise size={16} />
        </HoverBorderButton>
      </div>
      <LoaderLine loading={isBusy} />
      <div className="main-container sk:flex sk:flex-col sk:items-start sk:pt-5 sk:[&_h4]:text-[15px] sk:[&_h4]:leading-normal sk:[&_h4]:font-bold sk:[&_h4]:tracking-normal">
        <div className="sk:flex sk:w-full">
          <CalculationAccordion
            isDefault={isDefault}
            // @ts-expect-error - TS2322: Type '{ isDefault: { source_minimum_buffer: boolean; }; tabs: { season: boolean;...
            tabs={tabs}
            toggleTab={toggleTab}
            timeline={timeline}
            itemUPC={itemUPC}
            itemId={regionalItem}
            setLoading={setLoading}
            refreshTimeline={refreshTimeline}
            moq={timeline?.MOQ}
            shipping_buffer={timeline?.shipping_buffer}
            national={national}
            currentBuffer={currentBuffer}
            readOnly={!!readOnly}
            minDesiredStock={minDesiredStock}
            defaultValues={defaultValues}
            setDefaultValues={setDefaultValues}
            loading={isBusy}
            showLoadingBar={false}
            // @ts-expect-error - TS2339: Property 'primary_warehouse' does not exist on type 'never[]'.
            primaryWarehouse={data?.primary_warehouse}
          />
          <POWindowVelocityRenderer
            data={data}
            hardRefreshLoading={readOnly || loading}
            context="PO"
            loading={loading}
            setLoading={setLoading}
            refreshTimeline={refreshTimeline}
            itemId={itemId}
            title="Global Velocity Calculation"
          />
        </div>
        <div className="sk:mb-2 sk:w-full">
          <div className="sk:my-2 sk:h-px sk:w-full sk:bg-rule" />
          <div className="sk:mb-2">
            <div className="sk:flex sk:flex-wrap sk:items-center sk:justify-between sk:gap-3">
              <SegmentedTabs
                label="Timeline view"
                items={TAB_ITEMS}
                selectedKey={activeTab}
                onSelectionChange={setActiveTab}
              />
              <TabInformation>
                {!isOnSalesForecast &&
                  !!timeline?.total_prediction &&
                  // @ts-expect-error - TS2339: Property 'order_predictions' does not exist on type 'never[]'.
                  CommonUtility.isValidArray(data?.order_predictions) && (
                    <div>
                      <span className="label">
                        {renderLabelWithMarketplace('Suggested Order')}
                      </span>{' '}
                      <span className="value">
                        {CommonUtility.numberWithCommas(
                          timeline?.total_prediction,
                        )}
                        &nbsp;Units&nbsp;
                        {regionalSuggestedStockDaysLeft && (
                          <>({regionalSuggestedStockDaysLeft} Days)</>
                        )}
                      </span>
                    </div>
                  )}
                {!isOnSalesForecast &&
                  CommonUtility.isValidObject(currentStock) && (
                    <div>
                      <span className="label">
                        {renderLabelWithMarketplace('Current Stock')}
                      </span>{' '}
                      <span className="value">
                        {CommonUtility.numberWithCommas(
                          currentStock?.totalInventory || 0,
                        )}
                        &nbsp;Units&nbsp;
                        {regionalStockDaysLeft && (
                          <>({regionalStockDaysLeft} Days)</>
                        )}
                      </span>
                    </div>
                  )}
                {!isOnSalesForecast &&
                  CommonUtility.isValidArray(marketplaceDropdown) && (
                    <CountryDropdownStyle
                      selection
                      options={marketplaceDropdown}
                      onChange={(_, { value }) =>
                        setRegionalItem(String(value ?? ''))
                      }
                      value={regionalItem}
                      disabled={isBusy}
                    />
                  )}
                {isOnSalesForecast && showSalesForecast && (
                  <HoverBorderButton
                    className="sk:flex sk:items-center sk:justify-center"
                    disabled={!!(readOnly || loading) || addEventCount > 0}
                    onClick={() => setAddEventCount(prev => prev + 1)}
                    size="mini"
                  >
                    <Plus size={14} />
                    <span className="sk:ml-2">Event</span>
                  </HoverBorderButton>
                )}
                {isOnSalesForecast && showSalesForecast && (
                  <CountryDropdownStyle
                    selection
                    options={SALES_CHART_TYPES}
                    onChange={(_, { value }) =>
                      setChartType(String(value ?? ''))
                    }
                    value={chartType}
                    disabled={isBusy}
                    sorted={false}
                  />
                )}
              </TabInformation>
            </div>
            <TabContent>{dataTabContent?.[activeTab]}</TabContent>
          </div>
        </div>
      </div>
    </Drawer>
  );
}
